import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";

/**
 * Custom hook: payroll parameters aur branch-wise ESI/PF numbers padha.
 *
 * Returns:
 *   isMultiESI   : boolean  — MULTI ESI CODE parameter ON hai?
 *   isMultiPF    : boolean  — MULTI PF parameter ON hai?
 *   esiNoList    : string[] — Firm ki branches ke unique ESI numbers
 *   pfNoList     : string[] — Firm ki branches ke unique PF numbers
 *   paramsLoaded : boolean  — Loading complete hui?
 */
const usePayrollParams = () => {
  const dispatch = useDispatch();

  const [isMultiESI,   setIsMultiESI]   = useState(false);
  const [isMultiPF,    setIsMultiPF]    = useState(false);
  const [esiNoList,    setEsiNoList]    = useState([]);
  const [pfNoList,     setPfNoList]     = useState([]);
  const [paramsLoaded, setParamsLoaded] = useState(false);

  const loadParams = useCallback(async () => {
    try {
      // Firm ID localStorage se padho
      const firmData = JSON.parse(localStorage.getItem("selectedFirm") || "{}");
      const firmId = firmData?.Id;
      if (!firmId) {
        setParamsLoaded(true);
        return;
      }

      // 1. PayrollParameter fetch karo
      const params = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`
      );

      const p = params?.[0];
      const multiESI = !!p?.IsMultiESI;
      const multiPF  = !!p?.IsMultiPF;
      setIsMultiESI(multiESI);
      setIsMultiPF(multiPF);

      // 2. Agar MultiESI ya MultiPF ON hai to branches se ESI/PF numbers fetch karo
      if (multiESI || multiPF) {
        const branches = await Fn_FillListData(
          dispatch,
          () => {},
          "Data",
          `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${firmId}`
        );

        if (branches && Array.isArray(branches)) {
          // Unique ESI Nos (non-empty)
          const esiNos = [
            ...new Set(
              branches
                .map(b => (b.ESINo || "").trim())
                .filter(v => v !== "")
            )
          ];
          setEsiNoList(esiNos);

          // Unique PF Nos (non-empty)
          const pfNos = [
            ...new Set(
              branches
                .map(b => (b.PFNo || "").trim())
                .filter(v => v !== "")
            )
          ];
          setPfNoList(pfNos);
        }
      } else {
        setEsiNoList([]);
        setPfNoList([]);
      }
    } catch (err) {
      console.error("usePayrollParams: Error loading params", err);
    } finally {
      setParamsLoaded(true);
    }
  }, [dispatch]);

  useEffect(() => {
    loadParams();
  }, [loadParams]);

  // Firm change hone par reload karo
  useEffect(() => {
    const onStorage = () => {
      setParamsLoaded(false);
      loadParams();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, [loadParams]);

  return { isMultiESI, isMultiPF, esiNoList, pfNoList, paramsLoaded };
};

export default usePayrollParams;
